import { API_BASE, ENDPOINTS, getWsChatUrl } from '../config/api';
import PageShell from '../components/layout/PageShell';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/layout/PageHeader';

function CodeBlock({ children, title }: { children: string; title: string }) {
  return (
    <div className="rounded-lg border border-hairline overflow-hidden">
      <div className="px-4 py-2 text-xs font-semibold bg-surface-strong text-muted border-b border-hairline uppercase">
        {title}
      </div>
      <pre className="p-4 text-xs overflow-x-auto bg-canvas text-body font-mono whitespace-pre-wrap">
        {children}
      </pre>
    </div>
  );
}

const rows = [
  ['Auth', 'POST', ENDPOINTS.authLogin],
  ['Stations', 'GET', ENDPOINTS.stations],
  ['Devices', 'GET', ENDPOINTS.devicesList],
  ['Ask', 'POST', ENDPOINTS.chat],
  ['Control Chat', 'POST', ENDPOINTS.chatControl],
  ['Command', 'POST', ENDPOINTS.command(':command')],
  ['Usage Summary', 'GET', ENDPOINTS.usageHistory],
  ['Usage Tokens', 'GET', ENDPOINTS.usageLog],
];

export default function DocsPage() {
  const { t } = useTranslation();
  const base = API_BASE || '(same origin)';

  return (
    <PageShell>
      <PageHeader title={t('docs.pageTitle')} description={t('docs.pageDescription')} />

      <div className="space-y-8 pb-12">
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-ink">Base URL</h2>
          <div className="rounded-lg border border-hairline bg-surface-card p-4">
            <p className="text-sm text-body">
              Current API base: <code className="text-primary">{base}</code>
            </p>
            <p className="text-xs text-muted mt-2">
              `REACT_APP_API_BASE` should point at the gateway origin and must not end with `/api`.
            </p>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-bold text-ink">HTTP Endpoints</h2>
          <div className="overflow-x-auto rounded-lg border border-hairline">
            <table className="w-full text-sm text-left">
              <thead className="bg-surface-strong text-ink">
                <tr>
                  <th className="px-4 py-3">Area</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Path</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline bg-surface-card text-body">
                {rows.map(([area, method, path]) => (
                  <tr key={`${area}-${path}`}>
                    <td className="px-4 py-3 font-medium text-ink">{area}</td>
                    <td className="px-4 py-3 font-mono text-primary">{method}</td>
                    <td className="px-4 py-3 font-mono text-xs">{path}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CodeBlock title="Chat WebSocket">
{`CONNECT ${getWsChatUrl()}
{"token":"JWT","mode":"chat","message":"hello","conversation_id":"uuid"}`}
          </CodeBlock>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-bold text-ink">Local Run</h2>
          <CodeBlock title="Development stack">
{`docker compose -f docker-compose.dev.yml up --build
curl http://localhost:8001/healthz
curl http://localhost:8002/healthz`}
          </CodeBlock>
        </section>
      </div>
    </PageShell>
  );
}